import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import SectionCTA from "@/components/SectionCTA";
import { IMG, CONTACT, VAN } from "@/data/site";

const VALUES = [
  ["Quality materials", "We work with premium tiles and trusted materials for finishes that last."],
  ["Attention to detail", "Every line, cut and joint is set with obsessive precision."],
  ["Customer service", "Clear communication and a tidy, respectful approach in your home."],
  ["Domestic & commercial", "From family bathrooms to commercial spaces across the North West."],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About Rossi Ceramics" description="The story of Rossi Ceramics — over 60 years of tiling and bathroom craftsmanship in Stockport and the North West, led by Vincent Rossi." />
      <PageHero eyebrow="Our story" title="Craftsmanship, refined over decades" subtitle="Rossi Ceramics blends traditional tiling mastery with modern design thinking to transform homes across Stockport and the North West." image={VAN[0]} />

      <section className="container-rossi py-20 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <img src={IMG.craft[2]} alt="Vincent Rossi at work" className="rounded-3xl w-full h-[480px] object-cover border border-rossi-gold/20" />
        </Reveal>
        <Reveal delay={0.1}>
          <div>
            <p className="eyebrow mb-4">Meet Vincent Rossi</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-rossi-cream leading-tight">A personal commitment to every project</h2>
            <p className="mt-5 text-rossi-cream/75 leading-relaxed">
              With over 60 years of combined industry experience behind the Rossi Ceramics name, Vincent Rossi has built a reputation for reliability, skill and a genuine love of the craft. He is highly skilled, detail-focused and friendly — a specialist in transforming ordinary spaces into something extraordinary.
            </p>
            <p className="mt-4 text-rossi-cream/75 leading-relaxed">
              Vincent works directly with every client, from the first conversation to the final seal, ensuring the finished result is exactly right.
            </p>
            <blockquote className="mt-6 border-l-2 border-rossi-gold pl-5 font-serif text-xl text-rossi-cream/90 italic">
              “Every project deserves precision, creativity and craftsmanship.”
            </blockquote>
          </div>
        </Reveal>
      </section>

      <section className="container-rossi py-8">
        <div className="grid sm:grid-cols-2 gap-6">
          {VALUES.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.05}>
              <div className="card-lux p-7 flex gap-4">
                <span className="h-9 w-9 shrink-0 rounded-full bg-rossi-gold/15 text-rossi-gold flex items-center justify-center"><Check size={18} /></span>
                <div>
                  <h3 className="font-serif text-xl text-rossi-cream">{t}</h3>
                  <p className="mt-1 text-sm text-rossi-cream/70 leading-relaxed">{d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="container-rossi py-12">
        <Reveal>
          <div className="text-center mb-10">
            <p className="eyebrow justify-center mb-3">On the road across the North West</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-rossi-cream">Trusted local specialists</h2>
            <p className="text-sm text-rossi-gold mt-3 font-mono uppercase tracking-widest">{CONTACT.membership}</p>
            <p className="text-sm text-rossi-stone mt-1">{CONTACT.callout} · {CONTACT.address}</p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-6">
          {VAN.map((v, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <img src={v} alt={`Rossi Ceramics branded van ${i + 1}`} className="rounded-2xl w-full h-64 object-cover border border-rossi-gold/15" />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="container-rossi py-16 text-center">
        <Reveal>
          <p className="text-rossi-cream/75 max-w-2xl mx-auto">Ready to talk about your space? Call {CONTACT.owner} on {CONTACT.phone} or start online.</p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/services" className="btn-ghost-gold">Our services <ArrowRight size={16} /></Link>
            <Link to="/booking" className="btn-gold">Book a consultation</Link>
          </div>
        </Reveal>
      </section>

      <SectionCTA />
    </div>
  );
}
